"use client";

import Image from "next/image";
import Link from "next/link";
import { motion } from "framer-motion";
import { useRef, useState, useEffect } from "react";

const services = [
  {
    title: "UI/UX Design",
    description: "UI/UX Design, App Design, Website Design, Dashboard Design, Wireframing & Prototyping, Interaction Design, and Product Design.",
    href: "/services#uiux",
    box1: ["/assets/portfolio/uiux-design/16-analytics-platform-cover.webp", "/assets/portfolio/uiux-design/15-fintech-crm-cover.webp"],
    box2: ["/assets/portfolio/uiux-design/09-ecovise-esg-cover.webp", "/assets/portfolio/uiux-design/14-nexaai-workspace-cover.webp"],
    colors: ["#FFFFFF", "#2DD4A8"],
  },
  {
    title: "Web Development",
    description: "Full Stack Development, WordPress, React, Node.js, Custom Web Applications, E-commerce Solutions, API Integration.",
    href: "/services#webdev",
    box1: ["/assets/portfolio/web-development/01-agent-saas/01.png", "/assets/portfolio/web-development/09-healthcare-dental/01.png"],
    box2: ["/assets/portfolio/web-development/18-stylegen-ai-fashion/01.jpg", "/assets/portfolio/web-development/15-rigol-electronics/01.jpg"],
    colors: ["#1E40AF", "#A8C8F0"],
  },
  {
    title: "Graphic Design",
    description: "Logo Design, Brand Identity, Social Media Graphics, Marketing Materials, Business Branding, Visual Identity, Brand Strategy.",
    href: "/services#graphic",
    box1: ["/assets/portfolio/graphic-design/logo-03-vortex.jpg", "/assets/portfolio/graphic-design/brand-08-don-fuego.png"],
    box2: ["/assets/portfolio/graphic-design/poster-07-glass-typography.jpg", "/assets/portfolio/graphic-design/book-05-box-nordestino.jpg"],
    colors: ["#C2410C", "#2A2A2A"],
  },
  {
    title: "Presentation Design",
    description: "Pitch Decks, Investor Presentations, Sales Decks, Conference Keynotes, Corporate Presentations, and Interactive Slide Decks.",
    href: "/services#presentations",
    box1: ["/assets/portfolio/presentation-design/46-genesis-pitch-deck.png", "/assets/portfolio/presentation-design/53-digital-transformation.jpg"],
    box2: ["/assets/portfolio/presentation-design/65-hiring-platform.png", "/assets/portfolio/presentation-design/22-winning-slides.jpg"],
    colors: ["#2D1B4E", "#7DD3E8"],
  },
  {
    title: "Webflow & Framer",
    description: "No-code website development with Webflow and Framer. Blazing fast, SEO-optimized websites with custom animations and CMS.",
    href: "/services#webflow-framer",
    box1: ["/assets/portfolio/webflow-framer/04-marketing-agency.jpg", "/assets/portfolio/webflow-framer/12-sparktech-3d.png"],
    box2: ["/assets/portfolio/webflow-framer/14-numo-realestate.jpg", "/assets/portfolio/webflow-framer/20-stylegen-fashion.jpg"],
    colors: ["#166534", "#DC2626"],
  },
  {
    title: "AI & Chatbot Development",
    description: "Custom AI Chatbots, RAG Pipelines, LLM Fine-Tuning, AI-Powered Automation, and Intelligent Assistants for your business.",
    href: "/services#ai",
    box1: ["/assets/portfolio/ai-chatbots/02-ai-saas-dashboard.jpg", "/assets/portfolio/ai-chatbots/08-ai-buildhub.jpg"],
    box2: ["/assets/portfolio/ai-chatbots/13-vectra-ai-ecommerce.png", "/assets/portfolio/ai-chatbots/18-meeting-assistant.png"],
    colors: ["#CA8A04", "#7C3AED"],
  },
];

const marqueeItems = [
  "Framer", "Branding", "Dashboard", "Logos", "Webflow",
  "Slide Decks", "AI Chatbots", "Figma", "Social Media",
  "Pitch Decks", "React.js", "RAG Pipelines", "WordPress",
];

/* Colored portrait box with 2 landscape images stacked vertically */
function ServiceImageBox({ images, alt, color }: { images: string[]; alt: string; color: string }) {
  return (
    <div
      className="relative w-full md:w-[320px] lg:w-[396px] flex-shrink-0 rounded-2xl overflow-hidden aspect-[4/3] md:aspect-[396/556]"
      style={{
        background: `linear-gradient(160deg, ${color} 0%, ${color}cc 100%)`,
      }}
    >
      <div className="absolute inset-0 flex flex-col gap-2 p-3">
        {images.map((src, i) => (
          <div key={i} className="relative flex-1 rounded-md overflow-hidden shadow-xl group">
            <Image
              src={src}
              alt={`${alt} ${i + 1}`}
              fill
              sizes="(max-width: 640px) 100vw, (max-width: 768px) 50vw, 396px"
              className="object-cover group-hover:scale-105 transition-transform duration-500"
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function ServiceScroller() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const sectionRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observers: IntersectionObserver[] = [];
    sectionRefs.current.forEach((ref, i) => {
      if (!ref) return;
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) setActiveIndex(i);
        },
        { rootMargin: "-40% 0px -40% 0px", threshold: 0 }
      );
      observer.observe(ref);
      observers.push(observer);
    });
    return () => observers.forEach((o) => o.disconnect());
  }, []);

  return (
    <div ref={containerRef} className="relative">
      <div className="flex gap-4 sm:gap-6 md:gap-8 max-w-[1280px] mx-auto px-6">
        {/* Left side — sticky text that swaps on scroll */}
        <div className="hidden md:block w-[320px] lg:w-[396px] flex-shrink-0">
          <div className="sticky top-[120px] md:top-[140px] lg:top-[170px]">
            {services.map((service, i) => (
              <div
                key={i}
                className="absolute inset-0 transition-all duration-700 ease-[cubic-bezier(0.25,1,0.5,1)]"
                style={{
                  opacity: activeIndex === i ? 1 : 0,
                  transform: activeIndex === i ? "translateY(0) scale(1)" : activeIndex > i ? "translateY(-20px) scale(0.98)" : "translateY(20px) scale(0.98)",
                  filter: activeIndex === i ? "blur(0px)" : "blur(4px)",
                  pointerEvents: activeIndex === i ? "auto" : "none",
                }}
              >
                <h3 className="text-2xl sm:text-3xl md:text-[40px] font-bold mb-0 font-ui leading-tight">
                  <span>{service.title.split(" ")[0]} </span>
                  <span className="font-serif italic text-purple-light">
                    {service.title.split(" ").slice(1).join(" ")}
                  </span>
                </h3>

                <div
                  className="w-full max-w-[396px] h-px mt-4 mb-6"
                  style={{ background: "linear-gradient(90deg, #E11D24, #4A0A0C)" }}
                />

                <p className="text-foreground/80 text-sm leading-relaxed mb-6 font-body">
                  {service.description}
                </p>

                <Link
                  href={service.href}
                  className="inline-flex items-center gap-2 text-sm font-semibold transition-colors text-badge-text"
                >
                  See More
                  <span className="text-lg">→</span>
                </Link>
              </div>
            ))}
            {/* Invisible spacer for height */}
            <div className="invisible">
              <h3 className="text-2xl sm:text-3xl md:text-[40px] font-bold mb-0 font-ui leading-tight">Placeholder</h3>
              <div className="h-px mt-4 mb-6" />
              <p className="text-sm leading-relaxed mb-6">Placeholder text for height reservation</p>
              <span className="text-sm">See More →</span>
            </div>
          </div>
        </div>

        {/* Right side — scrolling image pairs in colored boxes */}
        <div className="flex-1 flex flex-col gap-8 md:gap-[50px]">
          {services.map((service, i) => (
            <motion.div
              key={i}
              ref={(el) => { sectionRefs.current[i] = el; }}
              className="flex flex-col gap-6"
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.25, 1, 0.5, 1] }}
              viewport={{ once: true, margin: "-100px" }}
            >
              {/* Mobile only: show title */}
              <div className="md:hidden">
                <h3 className="text-xl sm:text-2xl font-bold font-ui mb-2">{service.title}</h3>
                <p className="text-foreground/80 text-sm font-body mb-3">{service.description}</p>
                <Link href={service.href} className="inline-flex items-center gap-2 text-sm font-semibold mb-4 text-badge-text">
                  See More <span>→</span>
                </Link>
              </div>

              {/* Image pair — each box has 2 landscape images stacked */}
              <div className="flex flex-col md:flex-row gap-4 sm:gap-6 md:gap-8">
                <ServiceImageBox
                  images={service.box1}
                  alt={service.title}
                  color={service.colors[0]}
                />
                {/* Second box hidden on phone — one is enough, saves massive scroll distance */}
                <div className="hidden sm:block">
                  <ServiceImageBox
                    images={service.box2}
                    alt={`${service.title} showcase`}
                    color={service.colors[1]}
                  />
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Services() {
  return (
    <>
      {/* Services section */}
      <section className="py-12 sm:py-16 md:py-20 bg-surface">
        <div className="max-w-[1280px] mx-auto px-6 mb-8 md:mb-16">
          <motion.div
            className="text-left"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <p className="section-badge mb-4">What We Do</p>
            <h2 className="text-2xl sm:text-3xl md:text-[48px] font-bold leading-[1.2] font-ui">
              <span>We Design </span>
              <span className="font-serif italic text-purple-light">Brands</span>
              <span> That </span>
              <span className="font-serif italic text-purple-light">Speak</span>
              <span> To Audiences</span>
            </h2>
          </motion.div>
        </div>

        <ServiceScroller />
      </section>

      {/* Services marquee row 1 */}
      <div className="py-4 bg-surface border-y border-card-border overflow-hidden">
        <div className="marquee-track" style={{ "--marquee-duration": "25s" } as React.CSSProperties}>
          {[...marqueeItems, ...marqueeItems].map((item, i) => (
            <span key={i} className="flex-shrink-0 mx-3 sm:mx-6 text-sm sm:text-lg font-medium text-foreground/30 whitespace-nowrap">
              {item} <span className="mx-4 text-purple/50">•</span>
            </span>
          ))}
        </div>
      </div>

      {/* Services marquee row 2 — reverse */}
      <div className="py-4 bg-surface border-y border-card-border overflow-hidden">
        <div className="marquee-track-reverse" style={{ "--marquee-duration": "25s" } as React.CSSProperties}>
          {[...marqueeItems, ...marqueeItems].map((item, i) => (
            <span key={i} className="flex-shrink-0 mx-3 sm:mx-6 text-sm sm:text-lg font-medium text-foreground/30 whitespace-nowrap">
              {item} <span className="mx-4 text-purple/50">•</span>
            </span>
          ))}
        </div>
      </div>

      {/* "Book a Call" CTA below services */}
      <div className="py-8 bg-surface text-center">
        <span className="relative inline-block rounded-full p-[2px] gradient-btn-bg">
          <Link
            href="/contact"
            className="relative z-10 inline-flex items-center gap-2 px-8 py-4 rounded-full text-white font-semibold text-sm hover:opacity-90 transition-opacity"
            style={{
              background: "linear-gradient(rgb(180, 20, 28) 25%, rgb(225, 29, 36))",
            }}
          >
            Book a Call
            <Image src="/assets/arrows/CTA-Arrow-white.svg" alt="" width={14} height={14} />
          </Link>
        </span>
      </div>
    </>
  );
}
